/**
 * One time chart: stacked bars, lines, or both, over the buckets the service
 * sent. Geometry lives in chartGeometry.ts so it can be checked without a
 * browser; this file is the drawing and the interaction.
 *
 * What a reader can do, because a chart they cannot touch was the complaint:
 *
 *   hover        a crosshair on every chart at the same hour, and a readout of
 *                every series at that hour on the chart under the pointer
 *   drag         select a span, and the whole page zooms to it
 *   click a day  on a daily chart, zoom to that day by the hour
 *   legend       hide or show a series, and the axis rescales to what is left
 *   keyboard     focus a chart and walk it with the arrow keys
 *   table        every value as a table, because a tooltip must never be the
 *                only way to read one
 */

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react'
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import {
  band,
  barPath,
  barWidth,
  bucketAt,
  innerHeight,
  niceScale,
  pathSegments,
  plotFor,
  selectionRange,
  spanLabel,
  timeTicks,
  y,
} from './chartGeometry'

// ── shared hover ──────────────────────────────────────────────────────────

/**
 * Which bucket the reader is looking at, shared by every chart on the page.
 *
 * One crosshair across all panels is what lets a spike in refusals be read
 * against latency and lanes at the same hour without lining them up by eye.
 */
export const HoverContext = createContext<{
  index: number | null
  setIndex: (index: number | null) => void
}>({ index: null, setIndex: () => undefined })

export function HoverProvider({ children }: { children: ReactNode }) {
  const [index, setIndex] = useState<number | null>(null)
  const value = useMemo(() => ({ index, setIndex }), [index])
  return <HoverContext.Provider value={value}>{children}</HoverContext.Provider>
}

// ── measuring ─────────────────────────────────────────────────────────────

/** Measured, because a chart drawn into a fixed viewBox grows its text and
 *  its bars with the card. jsdom has no layout, so tests get a fixed width. */
function useMeasuredWidth(fallback = 640) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(fallback)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const measure = () => {
      const measured = element.getBoundingClientRect().width
      if (measured > 0) setWidth(measured)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, width }
}

// ── styles ────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  figure: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    margin: 0,
  },
  plot: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
  },
  svg: {
    display: 'block',
    overflow: 'visible',
    touchAction: 'none',
    userSelect: 'none',
    cursor: 'crosshair',
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      borderRadius: tokens.borderRadiusMedium,
    },
  },
  tick: {
    fill: tokens.colorNeutralForeground3,
    fontSize: '11px',
    fontVariantNumeric: 'tabular-nums',
  },
  tickMajor: {
    fill: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  grid: {
    stroke: tokens.colorNeutralStroke3,
    strokeWidth: 1,
    shapeRendering: 'crispEdges',
  },
  gridMajor: {
    stroke: tokens.colorNeutralStroke2,
  },
  baseline: {
    stroke: tokens.colorNeutralStroke1,
    strokeWidth: 1,
    shapeRendering: 'crispEdges',
  },
  reference: {
    stroke: tokens.colorPaletteRedBorder2,
    strokeWidth: 1,
    shapeRendering: 'crispEdges',
  },
  referenceLabel: {
    fill: tokens.colorNeutralForeground3,
    fontSize: '11px',
  },
  hoverBand: {
    fill: tokens.colorNeutralBackground1Hover,
  },
  crosshair: {
    stroke: tokens.colorNeutralForeground3,
    strokeWidth: 1,
    shapeRendering: 'crispEdges',
  },
  selection: {
    fill: tokens.colorBrandBackground2,
    stroke: tokens.colorBrandStroke1,
    strokeWidth: 1,
  },
  tooltip: {
    position: 'absolute',
    top: '4px',
    zIndex: 2,
    pointerEvents: 'none',
    minWidth: '150px',
    maxWidth: '240px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
  },
  tooltipTitle: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  tooltipRow: {
    display: 'grid',
    gridTemplateColumns: '14px auto 1fr',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  tooltipValue: {
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  tooltipName: {
    color: tokens.colorNeutralForeground3,
  },
  tooltipExtra: {
    marginTop: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
  },
  key: {
    display: 'inline-block',
    width: '12px',
    height: '2px',
    borderRadius: '1px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  legendItem: {
    minWidth: 0,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  legendOff: {
    color: tokens.colorNeutralForeground4,
    textDecorationLine: 'line-through',
  },
  swatchBar: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
  },
  swatchLine: {
    width: '14px',
    height: '2px',
    borderRadius: '1px',
  },
  tableToggle: {
    minWidth: 0,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  tableWrap: {
    maxHeight: '260px',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    '& th, & td': {
      padding: `4px ${tokens.spacingHorizontalS}`,
      textAlign: 'right',
      borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
      whiteSpace: 'nowrap',
    },
    '& th:first-child, & td:first-child': { textAlign: 'left' },
    '& thead th': {
      position: 'sticky',
      top: 0,
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground2,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
})

// ── the chart ─────────────────────────────────────────────────────────────

export interface Series {
  key: string
  name: string
  colour: string
  values: Array<number | null>
}

export interface TimeChartProps {
  /** Names the chart for a screen reader and captions its table. */
  title: string
  buckets: string[]
  bucketSeconds: number
  zone: string
  /** Stacked, bottom first. */
  bars?: Series[]
  lines?: Series[]
  height?: number
  /** How a value reads on the axis and in the readout. */
  format?: (value: number) => string
  /** A fixed axis, for a share that always runs 0 to 100 per cent. */
  scale?: { top: number; ticks: number[] }
  /** A line the values are read against, drawn only when it fits the axis. */
  reference?: { value: number; name: string }
  /** Extra lines under the readout, for what is not a series: a total, a share. */
  extra?: (index: number) => string | null
  /** A dragged span, or a clicked day, as the range the page should zoom to. */
  onSelect?: (from: string, to: string) => void
}

const defaultFormat = (value: number) => value.toLocaleString('en-GB')

export function TimeChart({
  title,
  buckets,
  bucketSeconds,
  zone,
  bars = [],
  lines = [],
  height = 200,
  format = defaultFormat,
  scale,
  reference,
  extra,
  onSelect,
}: TimeChartProps) {
  const styles = useStyles()
  const { ref, width } = useMeasuredWidth()
  const { index: hovered, setIndex } = useContext(HoverContext)
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [local, setLocal] = useState(false)
  const [drag, setDragState] = useState<{ from: number; to: number } | null>(null)
  // The drag is also held in a ref, read by the handlers. React state is only
  // current after a render, and pointer events can arrive faster than one: the
  // first version read the drag from state, a quick drag saw a span of zero on
  // release, and the zoom silently did not happen. Measured in the browser with
  // dispatched events on 2026-09-18.
  const dragRef = useRef<{ from: number; to: number } | null>(null)
  const setDrag = (next: { from: number; to: number } | null) => {
    dragRef.current = next
    setDragState(next)
  }
  const [showTable, setShowTable] = useState(false)

  const plot = plotFor(width, height)
  const count = buckets.length
  const shownBars = bars.filter((series) => !hidden.has(series.key))
  const shownLines = lines.filter((series) => !hidden.has(series.key))

  // The axis answers for what is visible. Hiding the tallest series lets the
  // rest be read at their own size, which is what toggling is for.
  const totals = buckets.map((_moment, index) =>
    shownBars.reduce((sum, series) => sum + (series.values[index] ?? 0), 0),
  )
  // A reference the caller chose to draw is part of what the axis must hold;
  // one it left out does not stretch the axis to reach it.
  const axis =
    scale ??
    niceScale([
      ...totals,
      ...shownLines.flatMap((series) => series.values),
      reference?.value ?? null,
    ])
  const referenceFits = reference !== undefined && reference.value <= axis.top
  const { width: bandWidth, at, centre } = band(count, plot)
  const thickness = barWidth(bandWidth)
  const ticks = timeTicks(buckets, bucketSeconds, zone, width - plot.padLeft - plot.padRight)
  const baseline = plot.padTop + innerHeight(plot)

  // Escape abandons a drag in progress, wherever the focus is.
  useEffect(() => {
    if (!drag) return
    const cancel = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      dragRef.current = null
      setDragState(null)
    }
    window.addEventListener('keydown', cancel)
    return () => window.removeEventListener('keydown', cancel)
  }, [drag])

  const indexFor = (event: PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    return bucketAt(event.clientX - box.left, count, plot)
  }

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const index = indexFor(event)
    setLocal(true)
    setIndex(index)
    if (dragRef.current) setDrag({ ...dragRef.current, to: index })
  }

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (!onSelect || event.button !== 0) return
    const index = indexFor(event)
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId)
    } catch {
      // A pointer the browser no longer tracks cannot be captured. The drag
      // still works while the pointer stays over the chart.
    }
    setDrag({ from: index, to: index })
  }

  const onPointerUp = (event: PointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const current = dragRef.current
    if (!current || !onSelect) return
    // The bucket under the pointer at release, not only the last move: a drag
    // released without a final move still ends where the pointer is.
    const released = { ...current, to: indexFor(event) }
    const span = Math.abs(released.to - released.from)
    setDrag(null)
    // A drag across two or more buckets zooms to them. A click on a day zooms
    // to that day by the hour. A click on an hour does nothing: there is no
    // finer bucket to show.
    if (span >= 1 || bucketSeconds >= 86400) {
      const range = selectionRange(released.from, released.to, buckets, bucketSeconds)
      if (range) onSelect(range.from, range.to)
    }
  }

  const onPointerLeave = () => {
    setLocal(false)
    setIndex(null)
  }

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (count === 0) return
    const current = hovered ?? count - 1
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      setLocal(true)
      setIndex(Math.max(0, current - 1))
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      setLocal(true)
      setIndex(Math.min(count - 1, current + 1))
    } else if (event.key === 'Escape') {
      setIndex(null)
    }
  }

  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const active = hovered !== null && hovered >= 0 && hovered < count ? hovered : null
  const tooltipLeft =
    active === null ? 0 : centre(active) > width / 2 ? centre(active) - 12 - 240 : centre(active) + 12

  return (
    <figure className={styles.figure}>
      <div ref={ref} className={styles.plot}>
        <svg
          className={styles.svg}
          width={plot.width}
          height={plot.height}
          viewBox={`0 0 ${plot.width} ${plot.height}`}
          role="img"
          aria-label={title}
          tabIndex={0}
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onFocus={() => {
            setLocal(true)
            if (hovered === null && count > 0) setIndex(count - 1)
          }}
          onBlur={() => {
            setLocal(false)
            setIndex(null)
          }}
          onKeyDown={onKeyDown}
        >
          {/* The value axis: hairline, solid, recessive. */}
          {axis.ticks.map((tick) => (
            <g key={`y-${tick}`}>
              <line
                className={styles.grid}
                x1={plot.padLeft}
                x2={plot.width - plot.padRight}
                y1={y(tick, axis.top, plot)}
                y2={y(tick, axis.top, plot)}
              />
              <text
                className={styles.tick}
                x={plot.padLeft - 6}
                y={y(tick, axis.top, plot) + 4}
                textAnchor="end"
              >
                {format(tick)}
              </text>
            </g>
          ))}

          {/* Day boundaries run the full height, so a reader sees where one day
              ends without counting labels. */}
          {ticks
            .filter((tick) => tick.major)
            .map((tick) => (
              <line
                key={`day-${tick.index}`}
                className={mergeClasses(styles.grid, styles.gridMajor)}
                x1={at(tick.index)}
                x2={at(tick.index)}
                y1={plot.padTop}
                y2={baseline}
              />
            ))}

          {active !== null ? (
            <rect
              className={styles.hoverBand}
              x={at(active)}
              width={bandWidth}
              y={plot.padTop}
              height={innerHeight(plot)}
            />
          ) : null}

          {drag ? (
            <rect
              className={styles.selection}
              data-testid="selection"
              x={at(Math.min(drag.from, drag.to))}
              width={(Math.abs(drag.to - drag.from) + 1) * bandWidth}
              y={plot.padTop}
              height={innerHeight(plot)}
            />
          ) : null}

          {buckets.map((_moment, index) => {
            let running = 0
            const stack = shownBars
              .map((series) => ({ series, value: series.values[index] ?? 0 }))
              .filter(({ value }) => value > 0)
            return stack.map(({ series, value }, position) => {
              const bottom = y(running, axis.top, plot)
              running += value
              const top = y(running, axis.top, plot)
              const isTop = position === stack.length - 1
              // A 2 px gap in the surface colour between segments, made by
              // leaving it out rather than by drawing a border round a mark.
              const gap = isTop ? 0 : 2
              return (
                <path
                  key={`${series.key}-${index}`}
                  data-series={series.key}
                  d={barPath(
                    at(index) + (bandWidth - thickness) / 2,
                    thickness,
                    Math.min(top + gap, bottom),
                    bottom,
                    isTop,
                  )}
                  fill={series.colour}
                />
              )
            })
          })}

          {referenceFits && reference ? (
            <g>
              <line
                className={styles.reference}
                x1={plot.padLeft}
                x2={plot.width - plot.padRight}
                y1={y(reference.value, axis.top, plot)}
                y2={y(reference.value, axis.top, plot)}
              />
              <text
                className={styles.referenceLabel}
                x={plot.width - plot.padRight}
                y={y(reference.value, axis.top, plot) - 4}
                textAnchor="end"
              >
                {reference.name}
              </text>
            </g>
          ) : null}

          {shownLines.map((series) =>
            pathSegments(series.values, axis.top, plot).map((d, n) => (
              <path
                key={`${series.key}-${n}`}
                data-series={series.key}
                d={d}
                fill="none"
                stroke={series.colour}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )),
          )}

          {/* A lone point between gaps draws no line, so it gets a dot: a value
              the line cannot show must still be visible somewhere on the chart. */}
          {shownLines.map((series) =>
            series.values.map((value, index) => {
              const before = index > 0 ? series.values[index - 1] : null
              const after = index < count - 1 ? series.values[index + 1] : null
              if (value === null || before !== null || after !== null) return null
              return (
                <circle
                  key={`${series.key}-dot-${index}`}
                  data-series={series.key}
                  cx={centre(index)}
                  cy={y(value, axis.top, plot)}
                  r={3}
                  fill={series.colour}
                />
              )
            }),
          )}

          {active !== null && shownLines.length > 0 ? (
            <line
              className={styles.crosshair}
              data-role="crosshair"
              x1={centre(active)}
              x2={centre(active)}
              y1={plot.padTop}
              y2={baseline}
            />
          ) : null}

          <line
            className={styles.baseline}
            x1={plot.padLeft}
            x2={plot.width - plot.padRight}
            y1={baseline}
            y2={baseline}
          />

          {ticks.map((tick) => (
            <text
              key={`x-${tick.index}`}
              className={mergeClasses(styles.tick, tick.major && styles.tickMajor)}
              x={centre(tick.index)}
              y={plot.height - 6}
              textAnchor="middle"
            >
              {tick.label}
            </text>
          ))}
        </svg>

        {active !== null && local ? (
          <div className={styles.tooltip} style={{ left: `${Math.max(0, tooltipLeft)}px` }} role="status">
            <div className={styles.tooltipTitle}>{spanLabel(buckets[active], bucketSeconds, zone)}</div>
            {[...shownBars].reverse().map((series) => (
              <div key={series.key} className={styles.tooltipRow}>
                <span className={styles.swatchBar} style={{ background: series.colour }} />
                <span className={styles.tooltipValue}>
                  {series.values[active] === null ? 'none' : format(series.values[active] ?? 0)}
                </span>
                <span className={styles.tooltipName}>{series.name}</span>
              </div>
            ))}
            {shownLines.map((series) => (
              <div key={series.key} className={styles.tooltipRow}>
                <span className={styles.key} style={{ background: series.colour }} />
                <span className={styles.tooltipValue}>
                  {series.values[active] === null ? 'nothing to measure' : format(series.values[active] ?? 0)}
                </span>
                <span className={styles.tooltipName}>{series.name}</span>
              </div>
            ))}
            {extra?.(active) ? <div className={styles.tooltipExtra}>{extra(active)}</div> : null}
          </div>
        ) : null}
      </div>

      <div className={styles.footer}>
        <div className={styles.legend}>
          {[...bars, ...lines].length > 1
            ? [...bars.map((s) => ({ s, kind: 'bar' as const })), ...lines.map((s) => ({ s, kind: 'line' as const }))].map(
                ({ s, kind }) => (
                  <Button
                    key={s.key}
                    size="small"
                    appearance="subtle"
                    className={mergeClasses(styles.legendItem, hidden.has(s.key) && styles.legendOff)}
                    aria-pressed={!hidden.has(s.key)}
                    onClick={() => toggle(s.key)}
                    title={hidden.has(s.key) ? `Show ${s.name}` : `Hide ${s.name}`}
                    icon={
                      <span
                        className={kind === 'bar' ? styles.swatchBar : styles.swatchLine}
                        style={{ background: hidden.has(s.key) ? tokens.colorNeutralStroke2 : s.colour }}
                      />
                    }
                  >
                    {s.name}
                  </Button>
                ),
              )
            : null}
        </div>
        <Button
          size="small"
          appearance="transparent"
          className={styles.tableToggle}
          onClick={() => setShowTable((shown) => !shown)}
          aria-expanded={showTable}
        >
          {showTable ? 'Hide the table' : 'Show as a table'}
        </Button>
      </div>

      {showTable ? (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label={`${title}, as a table`}>
            <thead>
              <tr>
                <th scope="col">{bucketSeconds >= 86400 ? 'Day' : 'Hour'}</th>
                {[...bars, ...lines].map((series) => (
                  <th key={series.key} scope="col">
                    {series.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets.map((moment, index) => (
                <tr key={moment}>
                  <td>{spanLabel(moment, bucketSeconds, zone)}</td>
                  {[...bars, ...lines].map((series) => (
                    <td key={series.key}>
                      {series.values[index] === null ? 'none' : format(series.values[index] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </figure>
  )
}
